import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { certificateService } from '@/services/certificate/certificate.service';
import type { CertificateGenerationLog } from '@/types/certificate.types';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';

interface CertificateFailedLogsProps {
  processId: string;
}

const CertificateFailedLogs: React.FC<CertificateFailedLogsProps> = ({ processId }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<CertificateGenerationLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);

  useEffect(() => {
    loadLogs();
  }, [processId]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await certificateService.getFailedGenerationLogsByProcess(processId);
      setLogs(data);
    } catch (error: any) {
      console.error('Error loading failed generation logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (applicantProcessId: string) => {
    try {
      setRetrying(applicantProcessId);
      const result = await certificateService.retryGeneration(applicantProcessId);
      if (result.generated) {
        toast.success(t('certificate.failedLogs.retrySuccess'));
        await loadLogs();
      } else {
        toast.error(t('certificate.failedLogs.retryConditionNotMet'));
      }
    } catch (error: any) {
      toast.error(t('certificate.failedLogs.retryFailed'));
      console.error(error);
    } finally {
      setRetrying(null);
    }
  };

  const failedLogs = logs.filter((l) => l.status === 'FAILED');

  if (loading || failedLogs.length === 0) return null;

  return (
    <div className="mt-4 space-y-2">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-red-600" />
        <h4 className="text-sm font-semibold text-red-800">
          {t('certificate.failedLogs.title')} ({failedLogs.length})
        </h4>
      </div>
      {failedLogs.map((log) => {
        const applicant = log.applicantProcess?.applicant;
        const applicantName = applicant?.firstName
          ? `${applicant.firstName} ${applicant.lastName || ''}`.trim()
          : applicant?.email ?? '—';

        return (
          <div
            key={log.id}
            className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-md"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{applicantName}</p>
              <p className="text-xs text-red-700 mt-0.5 truncate">
                {log.errorMessage || t('certificate.failedLogs.unknownError')}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {format(new Date(log.attemptedAt), 'yyyy-MM-dd HH:mm')}
                {log.retryCount > 0 && ` · ${log.retryCount} ${t('certificate.failedLogs.retries')}`}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRetry(log.applicantProcessId)}
              disabled={retrying === log.applicantProcessId}
              className="ml-3 shrink-0 border-red-300 text-red-700 hover:bg-red-100"
            >
              <RefreshCw className={`h-3 w-3 mr-1.5 ${retrying === log.applicantProcessId ? 'animate-spin' : ''}`} />
              {retrying === log.applicantProcessId
                ? t('certificate.failedLogs.retrying')
                : t('certificate.failedLogs.retry')}
            </Button>
          </div>
        );
      })}
    </div>
  );
};

export default CertificateFailedLogs;
